"use client";

import clsx from "clsx";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {usePreviewFetch} from "@/hooks/useFilePreview";
import {parseXlsxFromResponse, type ExcelSheet} from "@/utils/filePreview";
import ExcelTable from "./ExcelTable";

type Sheet = ExcelSheet;

const SheetView: React.FC<{data: string[][]}> = ({data}) => {
   const {t} = useTranslation("ask");
   if (!data || data.length === 0) {
      return (
         <div className="flex items-center justify-center h-40 text-xs text-500">
            {t("preview.emptySheet")}
         </div>
      );
   }
   return (
      <div className="rounded-lg border border-300 overflow-hidden bg-panel shadow-sm">
         <div className="overflow-auto max-h-full">
            <ExcelTable rows={data} density="full" />
         </div>
      </div>
   );
};

/**
 * Renders an xlsx spreadsheet. If the workbook has multiple sheets (either
 * fetched from `url` or parsed from a URL backed by a blob), tabs appear at
 * the bottom matching Excel's UI. Falls back to a single pre-parsed sheet
 * when only `data` is provided.
 */
const InlineExcelPreview: React.FC<{data?: string[][] | null; url?: string}> = ({data, url}) => {
   const {t} = useTranslation("ask");
   const [activeIdx, setActiveIdx] = useState(0);

   const {data: sheets, error} = usePreviewFetch<Sheet[]>(url, (res) =>
      parseXlsxFromResponse(res)
   );

   // Reset tab selection whenever we load a fresh workbook.
   useEffect(() => {
      if (sheets) setActiveIdx(0);
   }, [sheets]);

   if (error) {
      return (
         <div className="flex items-center justify-center h-full text-500 text-sm">
            {t("preview.failedToLoadSpreadsheet")}
         </div>
      );
   }

   const effectiveSheets: Sheet[] | null =
      sheets ?? (data ? [{name: "Sheet1", rows: data}] : null);

   if (!effectiveSheets) {
      return (
         <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-300 border-t-primary rounded-full animate-spin" />
         </div>
      );
   }

   const active = effectiveSheets[Math.min(activeIdx, effectiveSheets.length - 1)];
   const showTabs = effectiveSheets.length > 1;

   return (
      <div className="w-full h-full flex flex-col bg-100">
         <div className="flex-1 overflow-auto p-3">
            <SheetView data={active.rows} />
         </div>
         {showTabs && (
            <div className="flex-shrink-0 flex items-stretch gap-1 border-t border-300 bg-panel px-3 py-2 overflow-x-auto">
               {effectiveSheets.map((sheet, i) => {
                  const isActive = i === activeIdx;
                  return (
                     <button
                        key={sheet.name + i}
                        type="button"
                        onClick={() => setActiveIdx(i)}
                        className={clsx(
                           "px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors",
                           isActive
                              ? "bg-primary text-white font-semibold"
                              : "text-600 hover:bg-200 hover:text-800"
                        )}
                        title={sheet.name}
                     >
                        {sheet.name}
                     </button>
                  );
               })}
            </div>
         )}
      </div>
   );
};

export default InlineExcelPreview;
